<template>
  <div class="barcode-scanner">
    <qrcode-stream 
      v-if="!product && !loading"
      @detect="onDetect"
      @camera-on="onCameraOn" 
      @error="onError"
      :formats="barcodeFormats"
      :paused="paused"
      :constraints="cameraConstraints"
    ></qrcode-stream>
    
    <div v-if="loading" class="loading">
      <p>Loading product information...</p>
      <div class="spinner"></div>
    </div>
    
    <div v-if="error" class="error">
      <p>Error: {{ error }}</p>
      <button @click="scanAgain">Try Again</button>
    </div>

    <div v-if="!product && !loading && !error" class="instructions">
      <p>
        <strong>Barcode Scanning Tips:</strong><br>
        - Hold the barcode close to camera (5-10 cm)<br>
        - Ensure good lighting<br>
        - Keep the barcode straight<br>
        - Leave white space around the barcode<br>
        - If scanning fails, select barcode type manually
      </p>
    </div>

    <!-- Product Detail Page -->
    <div v-if="product" class="product-details">
      <div class="product-header">
        <div class="product-image" v-if="product.images && product.images.front">
          <img :src="product.images.front" :alt="product.productName || 'Product Image'">
        </div>
        <div class="product-image default-image" v-else>
          <span class="product-letter">{{ getProductInitial }}</span>
        </div>
        <div class="product-info">
          <h2>{{ product.productName || 'Unnamed Product' }}</h2>
          <p class="brand">{{ product.brand || '-' }}</p>
          
          <div class="health-score">
            <div class="score-badge" :class="getScoreClass(product.scores?.nutriScore)">
              {{ (product.scores?.nutriScore || 'U').toUpperCase() }}
            </div>
            <div class="score-label">Nutri-Score</div>
            <div class="score-explanation">
              {{ getNutriScoreExplanation(product.scores?.nutriScore) }}
            </div>
          </div>
          
          <div class="labels" v-if="product.labels && product.labels.length">
            <span v-for="label in product.labels" :key="label" class="label-badge">
              {{ formatLabel(label) }}
            </span>
          </div>
          <div class="labels" v-else>
            <span class="label-badge label-unknown">No labels available</span>
          </div>
        </div>
      </div>

      <div class="product-sections">
        <div class="section card ai-review-section" v-if="isAiLoading || aiSummary || aiError">
          <h3>AI Review</h3>
          <div v-if="isAiLoading" class="loading-ai">
            <div class="spinner-small"></div>
            <p>Our AI is analyzing the product...</p>
          </div>
          <div v-if="aiError" class="error-ai">
            <p>{{ aiError }}</p>
          </div>
          <div v-if="aiSummary" class="ai-review-content" @click="isAiSummaryExpanded = !isAiSummaryExpanded">
            <p class="ai-summary">
              <span class="summary-icon">💡</span>
              {{ aiSummary }}
            </p>
            <div v-if="isAiSummaryExpanded" class="ai-details">
              <p>{{ aiDetails }}</p>
            </div>
            <div class="expand-indicator" v-if="!isAiSummaryExpanded">
              Click to read more
            </div>
          </div>
        </div>
        
        <div class="section card">
          <h3>Ingredients</h3>
          <p class="ingredients-text">{{ product.ingredientsText || 'No ingredients information available' }}</p>
          
          <div class="ingredients-list" v-if="product.ingredients && product.ingredients.length">
            <h4>Main Ingredients:</h4>
            <div class="ingredients-grid">
              <div class="ingredient-item" v-for="ingredient in topIngredients" :key="ingredient.name">
                <div class="ingredient-name">{{ ingredient.name }}</div>
                <div class="ingredient-details">
                  <span class="ingredient-percent" v-if="ingredient.percent">{{ Math.round(ingredient.percent) }}%</span>
                  <span 
                    v-if="ingredient.vegan === 'yes'" 
                    class="vegan-badge" 
                    title="Vegan content">🌱</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="section allergens-section card">
          <h3>Allergens</h3>
          <div class="allergens-list" v-if="product.allergens && product.allergens.length">
            <span v-for="allergen in product.allergens" :key="allergen" class="allergen-badge">
              {{ formatAllergen(allergen) }}
            </span>
          </div>
          <div class="allergens-list" v-else>
            <span class="allergen-badge allergen-none">No allergens listed</span>
          </div>
          
          <div v-if="product.traces && product.traces.length">
            <h4>May Contain Traces Of:</h4>
            <div class="traces-list">
              <span v-for="trace in product.traces" :key="trace" class="trace-badge">
                {{ formatAllergen(trace) }}
              </span>
            </div>
          </div>
        </div>
        
        <div class="section nutrition-info card">
          <h3>Nutrition Facts</h3>
          <div class="nutrition-table" v-if="hasNutritionInfo">
            <div class="nutrition-row" v-if="product.nutrition.energy_kcal || product.nutrition.energy_kcal === 0">
              <div class="nutrition-label">Energy</div>
              <div class="nutrition-value">{{ product.nutrition.energy_kcal }} kcal</div>
            </div>
            <div class="nutrition-row" v-else>
              <div class="nutrition-label">Energy</div>
              <div class="nutrition-value">-</div>
            </div>
            
            <div class="nutrition-row" v-if="product.nutrition.fat || product.nutrition.fat === 0">
              <div class="nutrition-label">Fat</div>
              <div class="nutrition-value">{{ product.nutrition.fat }} g</div>
              <div class="nutrition-level" :class="getNutrientLevelClass(product.nutrientLevels?.fat)"></div>
            </div>
            <div class="nutrition-row" v-else>
              <div class="nutrition-label">Fat</div>
              <div class="nutrition-value">-</div>
            </div>
            
            <div class="nutrition-row" v-if="product.nutrition.saturated_fat || product.nutrition.saturated_fat === 0">
              <div class="nutrition-label">Saturated Fat</div>
              <div class="nutrition-value">{{ product.nutrition.saturated_fat }} g</div>
              <div class="nutrition-level" :class="getNutrientLevelClass(product.nutrientLevels?.saturatedFat)"></div>
            </div>
            <div class="nutrition-row" v-else>
              <div class="nutrition-label">Saturated Fat</div>
              <div class="nutrition-value">-</div>
            </div>
            
            <div class="nutrition-row" v-if="product.nutrition.sugars || product.nutrition.sugars === 0">
              <div class="nutrition-label">Sugars</div>
              <div class="nutrition-value">{{ product.nutrition.sugars }} g</div>
              <div class="nutrition-level" :class="getNutrientLevelClass(product.nutrientLevels?.sugars)"></div>
            </div>
            <div class="nutrition-row" v-else>
              <div class="nutrition-label">Sugars</div>
              <div class="nutrition-value">-</div>
            </div>
            
            <div class="nutrition-row" v-if="product.nutrition.salt || product.nutrition.salt === 0">
              <div class="nutrition-label">Salt</div>
              <div class="nutrition-value">{{ product.nutrition.salt }} g</div>
              <div class="nutrition-level" :class="getNutrientLevelClass(product.nutrientLevels?.salt)"></div>
            </div>
            <div class="nutrition-row" v-else>
              <div class="nutrition-label">Salt</div>
              <div class="nutrition-value">-</div>
            </div>
            
            <div class="nutrition-row" v-if="product.nutrition.proteins || product.nutrition.proteins === 0">
              <div class="nutrition-label">Protein</div>
              <div class="nutrition-value">{{ product.nutrition.proteins }} g</div>
            </div>
            <div class="nutrition-row" v-else>
              <div class="nutrition-label">Protein</div>
              <div class="nutrition-value">-</div>
            </div>
          </div>
          <div class="nutrition-missing" v-else>
            <p>No detailed nutrition information available</p>
          </div>
          
          <div class="additional-scores" v-if="product.scores">
            <div class="score-item">
              <span class="score-name">Eco-Score:</span>
              <span class="score-value" :class="'eco-' + getScoreClass(product.scores?.ecoScore)">
                {{ getCleanScoreValue(product.scores?.ecoScore) }}
              </span>
              <span class="score-desc">{{ product.scores?.ecoScore && product.scores.ecoScore !== 'not-applicable' ? 'Environmental impact' : 'Environmental impact unknown' }}</span>
            </div>
            
            <div class="score-item">
              <span class="score-name">NOVA Group:</span>
              <span class="score-value" :class="product.scores?.novaGroup ? 'nova-' + product.scores.novaGroup : 'nova-unknown'">
                {{ product.scores?.novaGroup || '?' }}
              </span>
              <span class="score-desc">{{ getNovaGroupExplanation(product.scores?.novaGroup) }}</span>
            </div>
          </div>
        </div>
        
        <div class="section categories-section card" v-if="product.categories && product.categories.length">
          <h3>Categories</h3>
          <div class="categories-list">
            <span v-for="category in product.categories" :key="category" class="category-badge">
              {{ formatCategory(category) }}
            </span>
          </div>
        </div>
        
        <div class="section packaging-section card" v-if="product.packaging && product.packaging !== 'unknown'">
          <h3>Packaging</h3>
          <p>{{ product.packaging }}</p>
        </div>
      </div>
      
      <div class="actions">
        <button @click="copyProductData" title="Copy product data as JSON" class="copy-data-btn">📋 Copy Data</button>
        <button @click="scanAgain" class="scan-again-btn">Scan Another Product</button>
      </div>
      <div v-if="copySuccessMessage" class="copy-success-message">
        {{ copySuccessMessage }}
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BarcodeScanner',
  data() {
    return {
      result: '',
      error: '',
      paused: false,
      loading: false,
      product: null,
      aiSummary: null,
      aiDetails: null,
      isAiLoading: false,
      aiError: '',
      isAiSummaryExpanded: false,
      copySuccessMessage: '',
      activeFormat: 'all',
      barcodeFormats: [
        'ean_13',     // Most food products
        'ean_8',      // Small food items
        'upc_a',      // American products
        'upc_e',      // American products (shortened)
        'code_128',   // Industrial barcodes
        'code_39',    // Industrial barcodes
        'qr_code'     // QR codes
      ],
      cameraConstraints: {
        width: { ideal: 1280 },
        height: { ideal: 720 },
        facingMode: 'environment'
      }
    }
  },
  computed: {
    topIngredients() {
      if (!this.product || !this.product.ingredients) return [];
      // Show only the most significant (highest percentage) 5 ingredients
      return this.product.ingredients
        .filter(ing => ing.percent > 0.5 || this.product.ingredients.length <= 5)
        .slice(0, 5);
    },
    hasNutritionInfo() {
      if (!this.product || !this.product.nutrition) return false;
      
      // We'll show nutrition info section even if values are 0 or missing
      return true;
    },
    getProductInitial() {
      if (!this.product || !this.product.productName) return '?';
      
      // Get the first letter of each word, uppercase
      const words = this.product.productName.split(' ');
      if (words.length === 1) {
        // If only one word, return first letter
        return this.product.productName.charAt(0).toUpperCase();
      } else if (words.length >= 2) {
        // If multiple words, return first letter of first two words
        return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
      }
      
      return this.product.productName.charAt(0).toUpperCase();
    }
  },
  methods: {
    updateFormats() {
      if (this.activeFormat === 'all') {
        this.barcodeFormats = [
          'ean_13', 'ean_8', 'upc_a', 'upc_e', 
          'code_128', 'code_39', 'qr_code'
        ];
      } else {
        // Use only selected format
        this.barcodeFormats = [this.activeFormat];
      }
      
      // Restart camera
      this.scanAgain();
      console.log('Active barcode formats:', this.barcodeFormats);
    },
    
    onDetect(detections) {
      console.log('Barcode detected:', detections);
      
      this.paused = true;
      
      if (detections && detections.length > 0) {
        const barcode = detections[0].rawValue;
        this.result = barcode;
        console.log('Scanned barcode value:', this.result);
        this.fetchProductData(barcode);
      }
    },
    
    fetchProductData(barcode) {
      this.loading = true;
      this.error = '';
      
      const apiUrl = 'https://us-central1-sharinbite.cloudfunctions.net/scanBarcodeHttp';
      
      fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ barcode })
      })
      .then(response => {
        if (!response.ok) {
          throw new Error('API did not respond');
        }
        return response.json();
      })
      .then(data => {
        if (data.success && data.data) {
          this.product = data.data;
          console.log('Product information:', this.product);
          this.fetchAiReview(this.product);
        } else {
          throw new Error('Product information not found');
        }
      })
      .catch(err => {
        console.error('API error:', err);
        this.error = `Could not retrieve product information: ${err.message}`;
      })
      .finally(() => {
        this.loading = false;
      });
    },
    
    fetchAiReview(productData) {
      this.isAiLoading = true;
      this.aiSummary = null;
      this.aiDetails = null;
      this.aiError = '';

      const apiUrl = 'https://us-central1-sharinbite.cloudfunctions.net/askOpenAIHttp';

      fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          prompt: {
            success: true,
            data: productData
          }
        })
      })
      .then(response => {
        if (!response.ok) {
          throw new Error('AI service API did not respond');
        }
        return response.json();
      })
      .then(data => {
        if (data.success && data.data) {
          this.aiSummary = data.data.summary;
          this.aiDetails = data.data.details;
        } else {
          throw new Error('AI review could not be generated.');
        }
      })
      .catch(err => {
        console.error('AI API error:', err);
        this.aiError = `Could not get AI review: ${err.message}`;
      })
      .finally(() => {
        this.isAiLoading = false;
      });
    },
    
    onCameraOn(promise) {
      console.log('Camera successfully activated');
      this.error = '';
      
      if (promise && typeof promise.then === 'function') {
        promise.then(capabilities => {
          console.log('Camera capabilities:', capabilities);
        }).catch(err => {
          console.error('Camera capability error:', err);
        });
      }
    },
    
    onError(error) {
      console.error('Camera error:', error);
      this.error = error.toString();
    },
    
    scanAgain() {
      this.result = '';
      this.paused = false;
      this.product = null;
      this.error = '';
      this.loading = false;
      this.aiSummary = null;
      this.aiDetails = null;
      this.isAiLoading = false;
      this.aiError = '';
      this.isAiSummaryExpanded = false;
      this.copySuccessMessage = '';
    },
    
    getScoreClass(score) {
      if (!score || score === 'not-applicable') return 'unknown';
      const scoreMap = {
        'a': 'excellent',
        'b': 'good',
        'c': 'moderate',
        'd': 'poor',
        'e': 'bad',
        'unknown': 'unknown'
      };
      return scoreMap[score.toLowerCase()] || 'unknown';
    },
    
    getNutriScoreExplanation(score) {
      if (!score) return 'Nutritional value unknown';
      
      const explanations = {
        'a': 'Excellent nutritional value',
        'b': 'Good nutritional value',
        'c': 'Average nutritional value',
        'd': 'Poor nutritional value',
        'e': 'Very poor nutritional value',
        'unknown': 'Nutritional value unknown'
      };
      
      return explanations[score.toLowerCase()] || 'Nutritional value unknown';
    },
    
    getNovaGroupExplanation(novaGroup) {
      const explanations = {
        1: 'Unprocessed or minimally processed foods',
        2: 'Processed culinary ingredients',
        3: 'Processed foods',
        4: 'Ultra-processed foods'
      };
      
      return explanations[novaGroup] || 'Processing level unknown';
    },
    
    getNutrientLevelClass(level) {
      if (!level || level === 'unknown') return 'unknown';
      
      const levelMap = {
        'low': 'good',
        'moderate': 'moderate',
        'high': 'poor'
      };
      
      return levelMap[level] || 'unknown';
    },
    
    formatLabel(label) {
      const labelMap = {
        'vegetarian': 'Vegetarian',
        'vegan': 'Vegan',
        'gluten_free': 'Gluten Free',
        'organic': 'Organic',
        'fair_trade': 'Fair Trade'
      };
      
      return labelMap[label] || label.charAt(0).toUpperCase() + label.slice(1);
    },
    
    formatAllergen(allergen) {
      const allergenMap = {
        'gluten': 'Gluten',
        'soybeans': 'Soybeans',
        'milk': 'Milk',
        'eggs': 'Eggs',
        'nuts': 'Nuts',
        'peanuts': 'Peanuts',
        'sesame': 'Sesame',
        'mustard': 'Mustard',
        'celery': 'Celery',
        'fish': 'Fish',
        'crustaceans': 'Crustaceans',
        'molluscs': 'Molluscs',
        'sulphites': 'Sulphites'
      };
      
      return allergenMap[allergen] || allergen.charAt(0).toUpperCase() + allergen.slice(1);
    },
    
    formatCategory(category) {
      return category.split('-').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
    },
    
    getCleanScoreValue(score) {
      if (!score || score === 'not-applicable') return '-';
      const scoreMap = {
        'a': 'A',
        'b': 'B',
        'c': 'C',
        'd': 'D',
        'e': 'E',
        'unknown': '-'
      };
      return scoreMap[score.toLowerCase()] || '-';
    },

    copyProductData() {
      if (!this.product) return;

      const dataToCopy = JSON.stringify(this.product, null, 2);
      navigator.clipboard.writeText(dataToCopy).then(() => {
        this.copySuccessMessage = 'Product data copied to clipboard!';
        setTimeout(() => {
          this.copySuccessMessage = '';
        }, 2500);
      }).catch(err => {
        console.error('Failed to copy product data: ', err);
        this.copySuccessMessage = 'Failed to copy data.';
        setTimeout(() => {
          this.copySuccessMessage = '';
        }, 2500);
      });
    }
  }
}
</script>

<style scoped>
.barcode-scanner {
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  padding: 10px;
}

.format-selector {
  margin-bottom: 15px;
  text-align: left;
}

.format-selector select {
  margin-left: 10px;
  padding: 5px;
  border-radius: 4px;
  border: 1px solid #ccc;
}

.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top-color: #4CAF50;
  animation: spin 1s ease-in-out infinite;
  margin: 20px auto;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.result, .error, .instructions {
  margin-top: 20px;
  padding: 15px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}

.result {
  background-color: #e8f5e9;
}

.error {
  color: #d32f2f;
  background-color: #ffebee;
}

.instructions {
  background-color: #f5f5f5;
  font-size: 0.9rem;
  line-height: 1.5;
}

/* Product Detail Page Styles */
.product-details {
  background: #f9f9f9;
  border-radius: 12px;
  margin-top: 20px;
  overflow: hidden;
  padding-bottom: 10px;
}

.product-header {
  display: flex;
  padding: 15px;
  background-color: white;
  border-radius: 12px;
  margin: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}

.product-image {
  width: 100px;
  height: 100px;
  margin-right: 15px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
}

.product-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-image.default-image {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #e0e0e0;
  color: #424242;
}

.product-letter {
  font-size: 2.5rem;
  font-weight: bold;
}

.product-info {
  flex: 1;
}

.product-info h2 {
  margin: 0 0 5px 0;
  font-size: 1.2rem;
  color: #333;
}

.brand {
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 10px;
}

.health-score {
  display: flex;
  align-items: center;
  margin: 10px 0;
}

.score-badge {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  margin-right: 10px;
  font-size: 1rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.score-badge.excellent { background-color: #1e8e3e; }
.score-badge.good { background-color: #7cb342; }
.score-badge.moderate { background-color: #ffb300; }
.score-badge.poor { background-color: #e65100; }
.score-badge.bad { background-color: #d32f2f; }
.score-badge.unknown { background-color: #757575; }

.score-label {
  font-weight: bold;
  margin-right: 10px;
}

.score-explanation {
  font-size: 0.8rem;
  color: #666;
}

.labels {
  display: flex;
  flex-wrap: wrap;
  margin-top: 10px;
}

.label-badge {
  background-color: #e0f2f1;
  color: #00796b;
  padding: 4px 10px;
  border-radius: 16px;
  font-size: 0.8rem;
  margin-right: 5px;
  margin-bottom: 5px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

.label-unknown {
  background-color: #f5f5f5;
  color: #757575;
}

.product-sections {
  padding: 0 10px;
}

.section {
  margin-bottom: 15px;
}

.card {
  background-color: white;
  border-radius: 12px;
  padding: 15px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}

.section h3 {
  margin: 0 0 10px 0;
  color: #333;
  font-size: 1.1rem;
  border-bottom: 1px solid #eee;
  padding-bottom: 8px;
}

.section h4 {
  margin: 12px 0 8px 0;
  font-size: 0.95rem;
  color: #555;
}

.ingredients-text {
  font-size: 0.9rem;
  line-height: 1.5;
  color: #666;
}

.ingredients-list {
  margin-top: 15px;
}

.ingredients-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 10px;
}

.ingredient-item {
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

.ingredient-name {
  font-weight: 500;
  margin-bottom: 5px;
}

.ingredient-details {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.ingredient-percent {
  color: #424242;
  font-size: 0.9rem;
  font-weight: bold;
  background-color: #e0e0e0;
  padding: 2px 6px;
  border-radius: 4px;
}

.vegan-badge {
  margin-left: 5px;
  font-size: 1rem;
}

.allergens-list, .traces-list, .categories-list {
  display: flex;
  flex-wrap: wrap;
}

.allergen-badge {
  background-color: #ffebee;
  color: #c62828;
  padding: 4px 10px;
  border-radius: 16px;
  font-size: 0.8rem;
  margin-right: 5px;
  margin-bottom: 5px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

.allergen-none {
  background-color: #e8f5e9;
  color: #2e7d32;
}

.trace-badge {
  background-color: #fff3e0;
  color: #e65100;
  padding: 4px 10px;
  border-radius: 16px;
  font-size: 0.8rem;
  margin-right: 5px;
  margin-bottom: 5px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

.category-badge {
  background-color: #e3f2fd;
  color: #1565c0;
  padding: 4px 10px;
  border-radius: 16px;
  font-size: 0.8rem;
  margin-right: 5px;
  margin-bottom: 5px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

.nutrition-info {
  margin-top: 15px;
}

.nutrition-table {
  width: 100%;
  border-spacing: 0;
}

.nutrition-row {
  display: flex;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;
}

.nutrition-label {
  flex: 1;
  font-size: 0.9rem;
}

.nutrition-value {
  width: 80px;
  text-align: right;
  font-size: 0.9rem;
  font-weight: bold;
  margin-right: 5px;
}

.nutrition-level {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-left: 10px;
  align-self: center;
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
}

.nutrition-level.good { background-color: #1e8e3e; }
.nutrition-level.moderate { background-color: #ffb300; }
.nutrition-level.poor { background-color: #d32f2f; }
.nutrition-level.unknown { display: none; }

.nutrition-missing {
  padding: 12px;
  background-color: #f5f5f5;
  border-radius: 8px;
  font-size: 0.9rem;
  color: #757575;
  text-align: center;
}

.additional-scores {
  margin-top: 15px;
  padding: 12px;
  background-color: #f5f5f5;
  border-radius: 8px;
}

.score-item {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.score-name {
  width: 100px;
  font-weight: bold;
  font-size: 0.85rem;
}

.score-value {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin-right: 10px;
  color: white;
  font-size: 0.85rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.score-value.eco-excellent { background-color: #1e8e3e; }
.score-value.eco-good { background-color: #7cb342; }
.score-value.eco-moderate { background-color: #ffb300; }
.score-value.eco-poor { background-color: #e65100; }
.score-value.eco-bad { background-color: #d32f2f; }
.score-value.eco-unknown { background-color: #757575; }

.score-value.nova-1 { background-color: #1e8e3e; }
.score-value.nova-2 { background-color: #7cb342; }
.score-value.nova-3 { background-color: #ffb300; }
.score-value.nova-4 { background-color: #d32f2f; }
.score-value.nova-unknown { background-color: #757575; }

.score-desc {
  font-size: 0.8rem;
  color: #666;
  flex: 1;
}

.actions {
  padding: 15px 15px 5px 15px;
  display: flex;
  justify-content: center;
  margin-top: 10px;
  flex-wrap: wrap;
}

.copy-success-message {
  text-align: center;
  color: #4CAF50;
  font-size: 0.9rem;
  padding: 5px 15px 10px 15px;
}

button {
  background: #4CAF50;
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: bold;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
  transition: all 0.2s ease;
}

button:hover {
  background: #45a049;
  box-shadow: 0 4px 8px rgba(0,0,0,0.15);
}

.copy-data-btn {
  background: #e0e0e0;
  color: #333;
  margin-right: 15px;
  margin-bottom: 10px;
}

.copy-data-btn:hover {
  background: #d5d5d5;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
}

.scan-again-btn {
  flex-grow: 1;
  max-width: 300px;
  margin-bottom: 10px;
}

/* Mobile Responsive Adjustments */
@media (max-width: 480px) {
  .product-header {
    flex-direction: column;
  }
  
  .product-image {
    margin-right: 0;
    margin-bottom: 15px;
    width: 120px;
    height: 120px;
  }
  
  .health-score {
    flex-wrap: wrap;
  }
  
  .score-badge {
    margin-bottom: 5px;
  }
  
  .nutrition-label {
    font-size: 0.85rem;
  }
  
  .nutrition-value {
    width: 70px;
    font-size: 0.85rem;
  }
  
  .score-item {
    flex-wrap: wrap;
  }
  
  .score-name {
    width: auto;
    margin-right: 10px;
  }
  
  .score-desc {
    width: 100%;
    margin-top: 5px;
    margin-left: 40px;
  }
}

/* AI Review Section styles */
.ai-review-section h3 {
  display: flex;
  align-items: center;
}

.ai-review-section h3::before {
  content: '🤖'; /* Robot emoji */
  margin-right: 8px;
  font-size: 1.2rem;
}

.loading-ai {
  display: flex;
  align-items: center;
  padding: 10px 0;
  font-size: 0.9rem;
  color: #666;
}

.spinner-small {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top-color: #4CAF50;
  animation: spin 1s ease-in-out infinite;
  margin-right: 10px;
}

.error-ai {
  color: #d32f2f;
  background-color: #ffebee;
  padding: 10px;
  border-radius: 8px;
  font-size: 0.9rem;
}

.ai-review-content {
  cursor: pointer;
  padding: 10px;
  border-radius: 8px;
  background-color: #e3f2fd;
  transition: background-color 0.3s;
}

.ai-review-content:hover {
  background-color: #d4eafd;
}

.ai-summary {
  font-weight: 500;
  line-height: 1.5;
  color: #1565c0;
  margin: 0;
}

.summary-icon {
  margin-right: 8px;
}

.ai-details {
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #bbdefb;
  font-size: 0.9rem;
  line-height: 1.6;
  color: #333;
}

.ai-details p {
  margin: 0;
}

.expand-indicator {
  margin-top: 10px;
  text-align: center;
  font-size: 0.8rem;
  color: #0d47a1;
  font-weight: bold;
}
</style> 